import React, { useMemo } from 'react'
import { ReactComponent as UserAvatarIcon } from '@assets/images/Icons/User_avatar.svg'
import type { AvatarProps } from './Avatar.types'
import { getInitialsFromName } from './Avatar.types'
import './Avatar.css'

const Avatar: React.FC<AvatarProps> = ({
  mode = 'solid',
  darkMode = false,
  color = 'gray',
  initials,
  name,
  src,
  alt,
  size = 48,
  className = '',
  style,
}) => {
  const displayInitials = useMemo(() => {
    if (initials?.trim()) {
      return initials.trim().slice(0, 2).toUpperCase()
    }
    if (name?.trim()) {
      return getInitialsFromName(name) || 'EN'
    }
    return 'EN'
  }, [initials, name])

  const label = alt ?? name ?? displayInitials
  const isIconVariant = color === 'avatar' && !src
  // Dark mode → tinted; light → solid (`mode`).
  const resolvedMode = darkMode ? 'tinted' : mode
  const sizeStyle = {
    ['--ds-avatar-size' as string]: `${size}px`,
    width: size,
    height: size,
    ...style,
  }

  if (src) {
    return (
      <span
        className={`ds-avatar ds-avatar--image${
          darkMode ? ' ds-avatar--dark' : ''
        } ${className}`.trim()}
        style={sizeStyle}
      >
        <img className="ds-avatar__image" src={src} alt={label} />
      </span>
    )
  }

  return (
    <span
      className={`ds-avatar ds-avatar--${resolvedMode} ds-avatar--color-${color}${
        darkMode ? ' ds-avatar--dark' : ''
      } ${className}`.trim()}
      style={sizeStyle}
      role="img"
      aria-label={label}
    >
      {isIconVariant ? (
        <UserAvatarIcon
          className="ds-avatar__icon"
          viewBox="2.4 2.5 11.2 11.4"
          aria-hidden="true"
        />
      ) : (
        <span className="ds-avatar__initials">{displayInitials}</span>
      )}
    </span>
  )
}

export default Avatar
